import { useMemo, useState } from "react";
import { CalendarDate } from "@internationalized/date";
import { DatePicker, Select } from "@its-engineering-and-development/ui-foundation/components";
import type { SortDirection } from "@/components/base/column-filter-popover";
import { useCurrencyPolicyRates } from "@app/hooks/use-policy";
import { POLICY_BASE_CURRENCY_OPTIONS } from "@app/types/policy";
import type { CurrencyRate, PolicyBaseCurrency } from "@app/types/policy";
import { PolicyLoadingPlaceholder, PolicyPageError, PolicyTableEmptyState, PolicyTableShell } from "./policy-table-header";
import { processPolicyRows } from "./policy-row-processing";
import { usePolicyTableColumnHeader } from "./use-policy-table-column-header";
import { usePolicyTablePagination } from "./use-policy-table-pagination";

const CURRENCY_SEARCH_HISTORY_KEY = "expense-admin:currency-policy-column-search-history";

const DEFAULT_RATE_DATE = new CalendarDate(2026, 6, 16);

type CurrencyColumnKey = "currency" | "currencyCode" | "exchangeRate";

function getCurrencyCellValue(rate: CurrencyRate, column: CurrencyColumnKey): string {
    switch (column) {
        case "currency":
            return rate.currency;
        case "currencyCode":
            return rate.currencyCode;
        case "exchangeRate":
            return rate.exchangeRate;
    }
}

function toDateKey(date: CalendarDate): string {
    const month = String(date.month).padStart(2, "0");
    const day = String(date.day).padStart(2, "0");
    return `${date.year}-${month}-${day}`;
}

function isPolicyBaseCurrency(value: string): value is PolicyBaseCurrency {
    return (POLICY_BASE_CURRENCY_OPTIONS as readonly string[]).includes(value);
}

export function CurrencyPolicyPage() {
    const [baseCurrency, setBaseCurrency] = useState<PolicyBaseCurrency>("USD");
    const [rateDate, setRateDate] = useState(DEFAULT_RATE_DATE);
    const [activeFilters, setActiveFilters] = useState<Partial<Record<CurrencyColumnKey, string>>>({});
    const [sort, setSort] = useState<{ by: CurrencyColumnKey; order: SortDirection } | null>(null);

    const rateDateKey = toDateKey(rateDate);
    const { rates: currencyRates, isLoading, error } = useCurrencyPolicyRates({ baseCurrency, rateDate: rateDateKey });

    const colHeader = usePolicyTableColumnHeader(
        CURRENCY_SEARCH_HISTORY_KEY,
        activeFilters,
        sort,
        (column, value) => setActiveFilters((prev) => ({ ...prev, [column]: value })),
        (column) =>
            setActiveFilters((prev) => {
                const next = { ...prev };
                delete next[column];
                return next;
            }),
        (column) =>
            setSort((prev) => {
                if (!prev || prev.by !== column) return { by: column, order: "asc" };
                if (prev.order === "asc") return { by: column, order: "desc" };
                return null;
            }),
    );

    const filteredRates = useMemo(() => processPolicyRows(currencyRates, getCurrencyCellValue, activeFilters, sort), [currencyRates, activeFilters, sort]);

    const hasActiveFilters = Object.keys(activeFilters).length > 0;
    const paginationResetKey = useMemo(
        () => `${baseCurrency}|${rateDateKey}|${JSON.stringify(activeFilters)}|${sort?.by ?? ""}|${sort?.order ?? ""}`,
        [activeFilters, baseCurrency, rateDateKey, sort],
    );
    const { paginatedRows, pageInfo, setPage } = usePolicyTablePagination(filteredRates, paginationResetKey);

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Currency Policy</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Currency exchange rates shown reflect XE.com rates as of the selected date and may be used during policy evaluation.
                </p>
            </div>

            <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-end sm:gap-6">
                <Select
                    label="Rates compared to"
                    selectedKey={baseCurrency}
                    onSelectionChange={(key) => {
                        if (!key) return;
                        const next = String(key);
                        if (isPolicyBaseCurrency(next)) {
                            setBaseCurrency(next);
                        }
                    }}
                    className="sm:max-w-xs"
                >
                    {POLICY_BASE_CURRENCY_OPTIONS.map((code) => (
                        <Select.Item key={code} id={code}>
                            {code}
                        </Select.Item>
                    ))}
                </Select>

                <div className="flex flex-col gap-1 sm:max-w-xs">
                    <span className="text-sm font-medium text-gray-700">Update to see exchange rates for new date.</span>
                    <DatePicker
                        aria-label="Update to see exchange rates for new date."
                        value={rateDate}
                        onChange={(nextDate) => {
                            if (!nextDate) return;
                            setRateDate(new CalendarDate(nextDate.year, nextDate.month, nextDate.day));
                        }}
                        className="w-full [&_button]:!h-11 [&_button]:!w-full [&_button]:!justify-between [&_button]:!rounded-lg [&_button]:!border [&_button]:!border-gray-300 [&_button]:!px-3 [&_button]:!text-md [&_button]:!font-normal [&_button]:!shadow-none"
                    />
                </div>
            </div>

            {error && <PolicyPageError message={error} />}

            {isLoading && currencyRates.length === 0 ? (
                <PolicyLoadingPlaceholder label="Loading currency rates…" />
            ) : (
                <PolicyTableShell minWidth="700px" pageInfo={pageInfo} onPageChange={setPage} isLoading={isLoading} loadingLabel="Loading currency rates…">
                    <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left">
                        <tr>
                            {colHeader("currency", "Currency")}
                            {colHeader("currencyCode", "Currency code")}
                            {colHeader("exchangeRate", "Exchange Rate")}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {!isLoading && filteredRates.length === 0 ? (
                            <PolicyTableEmptyState
                                colSpan={3}
                                message={hasActiveFilters ? "No currencies match the current filters." : "No currencies found."}
                            />
                        ) : (
                            paginatedRows.map((rate) => (
                                <tr key={rate.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-blue-600">{rate.currency}</td>
                                    <td className="px-4 py-3 text-gray-700">{rate.currencyCode}</td>
                                    <td className="px-4 py-3 text-gray-700">{rate.exchangeRate}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </PolicyTableShell>
            )}
        </div>
    );
}
